import { ApplicationInterface } from '@lib/classes/API.interface';
import { ReturnOnly } from '@lib/classes/ReturnOnly';
import { get, has, set, uniq } from 'lodash';
import { Moment } from 'moment';
import { Type } from './Types/Type';
import { Model } from './utils/Model';
import { TYPES } from './utils/types';

export class ModelClass {
    constructor(data: any = null) {
        this[TYPES.DATA] = this[TYPES.DATA] || {};
        this[TYPES.MODIFIED_PROPS] = this[TYPES.MODIFIED_PROPS] || [];

        if (data && data instanceof ModelClass) {
            data = data.$all();
        }

        this.$fill(data ? data : this.$defaults());

        if (!data) {
            this.resetModified();
        }
    }

    /**
     * Get model name
     * @returns {*}
     */
    $name() {
        return this[TYPES.NAME];
    }

    /**
     * Get defined prop of object
     * @param key
     * @param defaultValue
     */
    get(key?: string, defaultValue?: any): any {
        if (key) {
            if (this.has(key)) {
                return get(this[TYPES.DATA], key);
            }

            return defaultValue;
        }

        return { ...this[TYPES.DATA] };
    }

    /**
     * Set prop value to object
     * @param key
     * @param value
     */
    set(key: string, value: any): ModelClass {
        const $keys = this.$keys();

        if ($keys.includes(key)) {
            let Prop = this[TYPES.TYPES][key];

            if (Prop) {
                if (!Prop[TYPES.COLLECTION]) {
                    if (value instanceof Prop && value instanceof ModelClass) {
                        set(this[TYPES.DATA], key, value);
                    } else {
                        if (typeof Prop.toValue === 'function' || Type.isPrototypeOf(Prop)) {
                            set(this[TYPES.DATA], key, Prop.toValue(value));
                        } else if (typeof Prop.$fill === 'function' || ModelClass.isPrototypeOf(Prop)) {
                            const old = this.get(key);

                            if (old && (typeof old.$fill === 'function' || ModelClass.isPrototypeOf(old))) {
                                old.$fill(value);
                            } else {
                                const instance = new Prop();
                                instance.$fill(value);
                                set(this[TYPES.DATA], key, instance);
                            }
                        }
                    }
                }
            }

            // Properly activation of GC
            Prop = null;
        }

        this.setModified(key);

        return this;
    }

    /**
     * Check if object has prop defined
     * @param key
     */
    has(key: string) {
        return has(this[TYPES.DATA], key);
    }

    /**
     * Set modified property
     * @param prop
     * @returns {ModelClass}
     */
    setModified(prop: string) {
        this[TYPES.MODIFIED_PROPS] = [...this[TYPES.MODIFIED_PROPS], prop];
        return this;
    }

    /**
     * Reset modified
     * @returns {ModelClass}
     */
    resetModified() {
        this[TYPES.MODIFIED_PROPS] = [];
        return this;
    }

    /**
     * Fill model with data
     * @param data
     * @return {ModelClass}
     */
    $fill(data: null | { [key: string]: any } = null) {
        if (data) {
            Object.keys(data).forEach((key: string) => {
                this.set(key, data[key]);
            });
        }

        return this;
    }

    /**
     * Get Fields
     * @return {string}
     */
    $fields() {
        return ReturnOnly.stringify(this[TYPES.FIELDS]);
    }

    /**
     * Get Type
     * @param key
     * @param value
     * @return {*}
     */
    $type(key: string, value?: any): any {
        if (value) {
            this[TYPES.TYPES][key] = value;
        }
        return this[TYPES.TYPES][key];
    }

    $keys() {
        const keys = ([] as any).concat(
            Object.keys(this[TYPES.FIELDS] || {}),
            Object.keys(this[TYPES.DATA] || {}),
            Object.keys(this[TYPES.DEFAULTS] || {}),
        );

        return uniq<string>(keys).filter((key: string) => !/^__/.test(key) && !Object.keys(TYPES).includes(key));
    }

    /**
     * Get default values
     * @param deep
     * @return {*}
     */
    $defaults(deep = true) {
        const keys$ = this.$keys();

        return keys$.reduce((bag, key) => {
            // In case this key it's undefined we will just put an empty string
            let value = this.get(key);
            const type = this.$type(key);

            if (!value && type) {
                if (type[TYPES.COLLECTION]) {
                    value = [];
                } else if (typeof type.getDefault === 'function' || Type.isPrototypeOf(type)) {
                    value = type.getDefault();
                } else if (typeof type.defaults === 'function' || ModelClass.isPrototypeOf(type)) {
                    // In case of ModelClass's value and deep true, we will go to get defaults of
                    // children object, else, will end with an empty object.
                    if (deep) {
                        const instance = new type();
                        value = instance.$defaults(deep);
                    } else {
                        value = {};
                    }
                }
            }

            return {
                ...bag,
                [key]: value,
            };
        }, {});
    }

    /**
     * Define method
     * @param property
     * @param value
     * @param type
     * @return {ModelClass}
     */
    $define(property: string, value: any, type: null | string = null) {
        switch (type || property) {
            case 'method':
                if (!this[property] && typeof value === 'function') {
                    this[property] = value;
                }
                break;
            case 'endpoint':
                this.$property('endpoint', value);
                break;
            case 'property':
            default:
                this[property] = value;
        }

        return this;
    }

    /**
     * Alias of define method
     * @param property
     * @param callback
     * @return {*}
     */
    $method(property: string, callback: any) {
        return this.$define(property, callback, 'method');
    }

    /**
     * Alias of define method
     * @param property
     * @param value
     * @return {*}
     */
    $property(property: string, value: any) {
        return this.$define(property, value, 'property');
    }

    /**
     * Return all saved data
     * @return {*}
     */
    $all() {
        const data = this.get();

        Object.keys(data).forEach((key) => {
            const value = data[key];

            if (/_id$/i.test(key) && value <= 0) {
                delete data[key];
            }

            if (value && (typeof value.$all === 'function' || ModelClass.isPrototypeOf(value))) {
                data[key] = value.$all();
            }
        });

        return data;
    }

    /**
     * Detect & pick model objects
     */
    $changes(deep = true) {
        const exclude = Object.values(TYPES);
        const keys: string[] = uniq<string>(this[TYPES.MODIFIED_PROPS]).filter((prop: any) => !exclude.includes(prop));
        const data = {};

        keys.forEach((key: string) => {
            const value = this.get(key);

            if (deep && value && (typeof value.$changes === 'function' || ModelClass.isPrototypeOf(value))) {
                data[key] = value.$changes();
            } else {
                data[key] = value;
            }
        });

        return data;
    }
}

export type ModelClassType = typeof ModelClass & ApplicationInterface;

export { Model };

export interface ModelType {
    id: number;
    _type: string;
    created_at: Moment;
    updated_at: Moment;
}
